import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons/faArrowLeft';
import { faMessages } from '@fortawesome/pro-solid-svg-icons/faMessages';
import { AddressItemType, BatchAddressType, PostageUsageType } from '@ukpostbox/client-data';
import {
	Button,
	ButtonSize,
	ColourOutline,
	ColourStateOutline,
	ColourStateSolid,
	ContentBlock,
	ContentSidebarLayout,
	FlexAlign,
	FlexGap,
	FlexJustify,
	ItemRowHeader,
	PageHeaderNarrow,
	Pill,
	StackHorizontal,
	StackVertical,
	useLayout,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import { FormProvider, useForm } from '@ukpostbox/frontend-framework/lib/ReactHookForm';
import { FormatLocaleDate } from '@ukpostbox/helpers';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { AddressControl } from '../../_Global/Components/Address/AddressControl';
import { HarmonyCodeInfo } from '../Components/Controls/HarmonyCodeInfo';
import MailboxNavigation from '../../Mailboxes/Components/MailboxNavigation';
import { PostageControl } from '../../_Global/Components/PostageControl';
import { useForwardingBatchProvider } from '../Providers/ForwardingBatchProvider';
import { useSite } from '../../_Global/Providers/SiteInterfaceProvider';
import { BatchSectionType } from '../Types/ForwardingBatchTypes';
import { ForwardingBatchLoader } from '../Components/Loaders/ForwardingBatchLoader';
import { MailboxActions } from '../../Mailboxes/Components/MailboxActions';
import { RenderBatchSection } from '../Components/RenderBatchSection';

const DefaultBatchSections: Array<BatchSectionType> = [
	{
		sectionId: 0,
		name: 'Batch Details',
	},
	{
		sectionId: 1,
		name: 'Item Details',
		description:
			'Please ensure you tell us about everything your letters/parcels contain. You can add additional rows using the "Add Contents" button',
	},
	{
		sectionId: 2,
		name: 'Contents Declaration',
		description:
			'In the interest of safety to both UK Postbox And postal/courier staff, please tell us if your package/s contains any of the following. This information will also be used to ensure compliance with national, and international, transport regulations in relation to prohibited and dangerous goods.',
		hideForDocuments: true,
	},
	{
		sectionId: 3,
		name: 'Battery Declaration',
		description: 'This shipment contains cells or batteries in the following configuration:',
		hideForNonLithiumBatteries: true,
	},
	{
		sectionId: 4,
		name: 'Export Details',
		hideForDomestic: true,
	},
	{
		sectionId: 5,
		name: 'Notes',
		description: "If you'd like to include any notes to the mailroom, please add them in the field below.",
		isOptional: true,
	},
	{
		sectionId: 6,
		name: 'Documents',
		description: 'You can provide us with documents that will help us ship your batch.',
		isOptional: true,
	},
	{
		sectionId: 7,
		name: 'Legal',
		description: 'By forwarding these items, you state that:',
	},
	{
		sectionId: 8,
		name: 'Digital Signature',
		hideForDomestic: true,
	},
];

function ForwardingBatch() {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { isTiny, isSmall, isMedium, isLarge } = useLayout();
	const { updatePageMeta, currentLocale, UpdateSiteContact } = useSite();

	const {
		addressUpdating,
		postageUpdating,
		currentBatchData,
		currentBatchError,
		currentBatchStatus,
		documentsOnly,
		showHarmony,
		showCommercialInvoice,
		showCn22,
		containsLithiumBatteries,
		ValidateForwardingBatch,
		UpdateAssignedAddress,
		UpdateAssignedPostage,
	} = useForwardingBatchProvider();

	const formMethods = useForm({
		defaultValues: {
			addressId: currentBatchData?.address.addressId,
			postageId: currentBatchData?.postageOption.id,
		},
		mode: 'onChange',
	});

	const [activeAddressDetails, setActiveAddressDetails] = useState<AddressItemType | null>(null);

	const statusColour =
		currentBatchData?.status?.title === 'Forwarded'
			? ColourOutline.Success
			: currentBatchData?.status?.title === 'Pending'
				? ColourOutline.Warning
				: ColourOutline.Alert;

	const mailBoxesRef = useRef<any>(null);
	const mailBoxNavigationRef = useRef<any>(null);

	const batchReadOnly =
		currentBatchData?.locked ||
		(currentBatchData?.status?.id && [2, 99].includes(currentBatchData?.status?.id)) ||
		false;
	const batchComplete = currentBatchData?.status?.id === 5;

	let displaySectionIndex = 0;

	const HandleAddressChanged = (address: AddressItemType) => {
		if (address.object) {
			setActiveAddressDetails(address);
			UpdateAssignedAddress(address.object as unknown as BatchAddressType);
		}
	};

	useEffect(() => {
		updatePageMeta({
			title: currentBatchData?.batchIdNo
				? `${currentBatchData?.batchIdNo} - ${t('forwardingBatches.title')}`
				: t('forwardingBatches.title'),
		});
	}, [currentLocale, currentBatchStatus, currentBatchData]);

	return (
		<ContentSidebarLayout
			ref={mailBoxesRef}
			navigationComponent={
				<MailboxNavigation
					ref={mailBoxNavigationRef}
					title={t('forwardingBatches.title')}
					addonContent={(isTiny || isSmall || isMedium) && <MailboxActions />}
				/>
			}
			extraContentClass='flex-column forwarding-batch-rows'
		>
			{currentBatchStatus === 'pending' ? (
				<ForwardingBatchLoader />
			) : currentBatchStatus === 'error' ? (
				<ContentBlock
					error
					stacked
					title={currentBatchError?.status as string}
					content={currentBatchError?.data.error as string}
					extraClass='m-md'
				/>
			) : (
				<div className='sticky'>
					<PageHeaderNarrow
						title={currentBatchData?.batchIdNo as string}
						secondaryHeader
						descriptionContent={
							currentBatchData?.status.description ? (
								<>
									<h6 className={`text-white ${currentBatchData.status.adminNotes ? '' : 'm-0'}`}>
										{currentBatchData.status.description}
									</h6>

									{currentBatchData.status.adminNotes && (
										<h6
											className='m-0 text-white'
											dangerouslySetInnerHTML={{
												__html: currentBatchData.status.adminNotes as string,
											}}
										/>
									)}
								</>
							) : (
								<StackHorizontal gap={FlexGap.ExtraSmall}>
									<Pill
										content={t('status.dynamicType', { action: currentBatchData?.status?.title })}
										colour={statusColour}
									/>
									<Pill
										content={
											currentBatchData?.lastChanged
												? t('forwardingBatches.labels.lastChanged', {
														date: FormatLocaleDate(
															currentBatchData?.lastChanged,
															currentLocale,
														),
													})
												: ''
										}
										colour={ColourOutline.Secondary}
									/>
								</StackHorizontal>
							)
						}
						preTitleAddon={
							<Button
								title={t('actions.goBack')}
								onClick={() => navigate(-1)}
								iconOnly
								icon={faArrowLeft}
							/>
						}
						postTitleAddon={
							<>
								{(isTiny || isSmall || isMedium) && (
									<div className='mail-item-menu ml-a'>
										<Button title='Item Actions' squared icon={faMessages} iconOnly showArrow />
									</div>
								)}

								{isMedium && (
									<StackHorizontal gap={FlexGap.Small} justify={FlexJustify.End}>
										<Button
											title='Message Us About this Item'
											onClick={() =>
												UpdateSiteContact(
													true,
													3,
													undefined,
													`With regards to forwarding batch: ${currentBatchData?.batchIdNo}`,
												)
											}
											squared
											icon={faMessages}
											colour={
												currentBatchData?.status.description
													? ColourStateSolid.Warning
													: ColourStateOutline.Secondary
											}
										/>
									</StackHorizontal>
								)}
							</>
						}
						extraClass={`mailbox-page-header ${currentBatchData?.status.description ? 'mail-item-issue from-left show-issues' : ''}`}
					/>

					<FormProvider {...formMethods}>
						<ItemRowHeader title='Forwarding Batch List Header' extraClass='mail-item-toolbar p-md'>
							<div className='container-narrow'>
								<div className='row'>
									<div className='col-xs-12 col-md-4'>
										<StackVertical>
											<div className='h6 mb-xs'>Forwarding To:</div>
											{batchComplete || batchReadOnly ? (
												<span>{currentBatchData?.address.formatted}</span>
											) : (
												<AddressControl
													name='addressId'
													showCreateAddressOptions={!batchComplete}
													forwardItemCallback={(item) => HandleAddressChanged(item)}
													minimal
													defaultValues={
														currentBatchData?.address.addressId !== -1
															? [currentBatchData?.address.addressId]
															: undefined
													}
													extraForwardingAddressData={currentBatchData?.address}
													singleSelection
													loading={addressUpdating}
												/>
											)}
										</StackVertical>
									</div>
									<div className='col-xs-12 col-md-4'>
										<StackVertical>
											<div className='h6 mb-xs'>Postage Service Requested:</div>
											{batchComplete || batchReadOnly ? (
												<span>{currentBatchData?.postageOption.value}</span>
											) : (
												<PostageControl
													name='postageId'
													postageType={PostageUsageType.Forwarding}
													activeAddress={activeAddressDetails}
													forwardItemCallback={(item) => UpdateAssignedPostage(item)}
													minimal
													defaultValues={
														currentBatchData?.postageOption.id !== -1
															? [currentBatchData?.postageOption.id]
															: undefined
													}
													singleSelection
													loading={postageUpdating}
												/>
											)}
										</StackVertical>
									</div>
									<div className='col-xs-12 col-md-4'>
										{currentBatchData?.splits && (
											<StackVertical>
												<div className='h6 mb-xs'>Tracking Details:</div>
												<StackVertical gap={FlexGap.ExtraSmall}>
													{currentBatchData?.splits &&
														currentBatchData?.splits.map((split, index) => (
															<StackHorizontal
																gap={FlexGap.Small}
																align={FlexAlign.Center}
																justify={FlexJustify.Between}
																fill
															>
																<div>Shipment {index + 1}</div>
																<div>{split?.externalTrackingId}</div>
																<Button
																	size={ButtonSize.Small}
																	title='View Tracking'
																	actionLink={{
																		title: 'View Tracking',
																		url: split.externalTrackingUrl,
																		target: '_blank',
																	}}
																/>
															</StackHorizontal>
														))}
												</StackVertical>
											</StackVertical>
										)}

										{!!currentBatchData?.trackingNumber && currentBatchData?.trackingNumbers && (
											<StackVertical>
												<div className='h6 mb-xs sm-sub-heading'>Tracking Details:</div>
												{!!currentBatchData?.trackingNumber && (
													<div>{currentBatchData?.trackingNumber}</div>
												)}
												{currentBatchData?.trackingNumbers && (
													<div>{currentBatchData?.trackingNumbers.toString()}</div>
												)}
											</StackVertical>
										)}
									</div>
								</div>
							</div>
						</ItemRowHeader>

						<div className='column-adjuster pl-md pr-md'>
							<div className='container-narrow'>
								<div className='row'>
									<div className='col-xs-12'>
										<div className='flex-column flex-gap-xl mt-xxl mb-xxl'>
											{!batchComplete && !documentsOnly && showHarmony && <HarmonyCodeInfo />}

											{DefaultBatchSections.map((section) => {
												if (
													(section.sectionId === 8 && documentsOnly) ||
													(section.sectionId === 8 &&
														!showCommercialInvoice &&
														!showCn22 &&
														!containsLithiumBatteries)
												)
													return null;
												if (section.sectionId === 4 && !showCommercialInvoice) return null;
												if (
													(section.sectionId === 3 && documentsOnly) ||
													(section.sectionId === 3 && !containsLithiumBatteries)
												)
													return null;
												if (section.sectionId === 2 && documentsOnly) return null;
												if (section.sectionId === 0 && batchComplete) return null;

												displaySectionIndex += 1;

												return (
													<RenderBatchSection
														key={`batchSection${section.sectionId}`}
														section={section}
														sectionIndex={displaySectionIndex}
														readOnly={batchReadOnly}
														complete={batchComplete}
													/>
												);
											})}

											{!batchComplete && currentBatchData?.batchId && (
												<Button
													title='Forward Items'
													colour={ColourStateSolid.Success}
													fullWidth
													disabled={batchReadOnly || !formMethods.formState.isValid}
													onClick={() => ValidateForwardingBatch()}
												/>
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					</FormProvider>
				</div>
			)}
		</ContentSidebarLayout>
	);
}

export default ForwardingBatch;
